export interface CitySearchResult {
  id: number;
  name: string;
  admin1?: string;
  country?: string;
  countryCode?: string;
  latitude: number;
  longitude: number;
  timeZone: string;
  population?: number;
}

interface OpenMeteoLocation {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  timezone?: string;
  country?: string;
  country_code?: string;
  admin1?: string;
  population?: number;
}

interface OpenMeteoResponse {
  results?: OpenMeteoLocation[];
}

const CACHE_KEY = '4eternity.city-search.v1';
const MAX_CACHED_CITIES = 300;

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase();
}

function loadCache(): CitySearchResult[] {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CitySearchResult[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveCache(cities: CitySearchResult[]): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cities.slice(0, MAX_CACHED_CITIES)));
  } catch {
    // Storage can be unavailable in private/restricted browser modes.
  }
}

function deduplicate(cities: CitySearchResult[]): CitySearchResult[] {
  const map = new Map<number | string, CitySearchResult>();
  for (const city of cities) {
    const key = city.id || `${city.name}-${city.latitude}-${city.longitude}`;
    if (!map.has(key)) map.set(key, city);
  }
  return [...map.values()];
}

function rankResults(cities: CitySearchResult[], query: string): CitySearchResult[] {
  const needle = normalize(query);
  return cities
    .filter((city) => {
      const haystack = normalize(`${city.name} ${city.admin1 ?? ''} ${city.country ?? ''}`);
      return haystack.includes(needle);
    })
    .sort((a, b) => {
      const aName = normalize(a.name);
      const bName = normalize(b.name);
      const aStarts = aName.startsWith(needle) ? 1 : 0;
      const bStarts = bName.startsWith(needle) ? 1 : 0;
      if (aStarts !== bStarts) return bStarts - aStarts;
      return (b.population ?? 0) - (a.population ?? 0);
    });
}

export function cachedCitySearch(query: string): CitySearchResult[] {
  if (query.trim().length < 2) return [];
  return rankResults(loadCache(), query).slice(0, 30);
}

export async function searchCities(
  query: string,
  language = 'de',
  signal?: AbortSignal,
): Promise<{ results: CitySearchResult[]; offlineOnly: boolean }> {
  const trimmed = query.trim();
  const cached = cachedCitySearch(trimmed);
  if (trimmed.length < 2) return { results: cached, offlineOnly: !navigator.onLine };
  if (!navigator.onLine) return { results: cached, offlineOnly: true };

  // Open-Meteo's geocoder supports worldwide city/postcode lookup and returns
  // WGS84 coordinates plus the IANA timezone. Three or more characters use
  // prefix matching; our local cache additionally supports substring matching.
  const url = new URL('https://geocoding-api.open-meteo.com/v1/search');
  url.searchParams.set('name', trimmed);
  url.searchParams.set('count', '50');
  url.searchParams.set('language', language.toLowerCase().slice(0, 2));
  url.searchParams.set('format', 'json');

  const response = await fetch(url, { signal, headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Städtesuche fehlgeschlagen (${response.status})`);
  const payload = (await response.json()) as OpenMeteoResponse;
  const online = (payload.results ?? [])
    .filter((entry) => Number.isFinite(entry.latitude) && Number.isFinite(entry.longitude))
    .map<CitySearchResult>((entry) => ({
      id: entry.id,
      name: entry.name,
      admin1: entry.admin1,
      country: entry.country,
      countryCode: entry.country_code,
      latitude: entry.latitude,
      longitude: entry.longitude,
      timeZone: entry.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      population: entry.population,
    }));

  const combined = deduplicate([...online, ...loadCache()]);
  saveCache(combined);
  return { results: rankResults(deduplicate([...online, ...cached]), trimmed).slice(0, 30), offlineOnly: false };
}

export function rememberCity(city: CitySearchResult): void {
  saveCache(deduplicate([city, ...loadCache()]));
}
